import React, {useMemo} from 'react';
import PropTypes from 'prop-types';
import i18n from '../../utils/i18n';
import cx from 'classnames';
import {
  documentType,
  issueAttributesType,
  oxygenType,
  oxygenUserType,
  statusOptionsArrayType
} from '../../constants/proptype-validation';
import Button from '@adsk/bim360-matrix-react-button';
import Label from '@adsk/bim360-matrix-react-label';
import MentionInput from '@adsk/bim360-matrix-react-mention-input';
import {StatusColorIndicatorOption} from '@adsk/bim360-matrix-react-status-color-indicator';
import {STATUS_CLOSED, STATUS_VOID, statusColorMapping} from '../../constants/status-types';
import {viewTypes} from '../../constants/view-types';
import {ISSUE_ATTRIBUTE_NAMES} from '../../constants/issue-attribute-names';
import {getUserCompanyName} from '../../utils/userHelpers';
import {ActivityBatchReadTypes} from '../../constants/activity';
import {canAddComment, getLinkedDocument, hasLinkedDocument, hasPermittedAttribute} from '../../utils/issues-helpers';
import lodashPick from 'lodash/pick';
import isBoolean from 'lodash/isBoolean';
import lodashCloneDeep from 'lodash/cloneDeep';
import {Formik} from 'formik';
import useCurrentIssuePreviewPopup from './useCurrentIssuePreviewPopup';
import {fetchActivities, updateIssueAction} from '../../app/actions';
import {
  getActiveUsers,
  getAllOxygenMappings,
  getCurrentIssuePreviewStatusOptions,
  getCurrentPreviewIssueAttributes,
  getCurrentPreviewIssueDocument,
  getCurrentUserInMcFormat,
  getCurrentViewType,
  getGroupedProjectActiveEntitiesForMentionComponent,
  getProjectCompanies,
  getProjectRoles,
  getRequiredFieldsFormatted
} from '../../app/selectors';
import {connect} from '../../app/connect';
import featureFlags, {FEATURE_FLAGS} from '../../utils/featureFlags';
import SelectBoxAdapter from '../common/SelectBoxAdapter';
import {useAllowedAssignees} from '../forms/useAllowedAssignees';

const DROPDOWN_MAX_WIDTH = 348;

const getAllowedLinkedDocumentPropertiesOnClose = () =>
  featureFlags.get(FEATURE_FLAGS.ISSUES_V2) ? ['type', 'urn', 'createdAtVersion', 'closedAtVersion', 'details'] : [];

const IssuePreviewTransitionPopUpForm = ({
  issue,
  document,
  changeToStatus,
  issueStatusOptions,
  groupedProjectEntitiesForMention,
  onStatusChange,
  dispatch,
  viewType,
  users,
  companies,
  roles,
  currentUser,
  oxygen,
  requiredFieldsMap
}) => {
  const {options: assigneeOptions, clearable: isAssigneeClearable} = useAllowedAssignees({
    users,
    companies,
    roles,
    currentUser,
    oxygen,
    issue
  });
  const doc = getLinkedDocument(issue);
  const documentVersion = document ? document.version : doc.createdAtVersion; // Fallback for when the document does not exist
  const isClosingIssueWithDocument =
    (changeToStatus === STATUS_CLOSED || changeToStatus === STATUS_VOID) &&
    hasPermittedAttribute(issue, ISSUE_ATTRIBUTE_NAMES.LINKED_DOCUMENTS) &&
    hasLinkedDocument(issue);
  const initialValues = useMemo(() => {
    const issueDocProps = {};

    if (isClosingIssueWithDocument) {
      issueDocProps[ISSUE_ATTRIBUTE_NAMES.LINKED_DOCUMENTS] = [
        {
          ...lodashPick(lodashCloneDeep(doc), getAllowedLinkedDocumentPropertiesOnClose()),
          closedAtVersion: documentVersion
        }
      ];

      // Define is3D attribute for backward compatibility of Issues V1
      const linkedDoc = issueDocProps[ISSUE_ATTRIBUTE_NAMES.LINKED_DOCUMENTS][0];
      if (linkedDoc.details?.viewable && !isBoolean(linkedDoc.details.viewable.is3D)) {
        linkedDoc.details.viewable.is3D = !!linkedDoc.details.position?.z && linkedDoc.details.position.z !== 0;
      }
    }

    return {
      [ISSUE_ATTRIBUTE_NAMES.STATUS]: changeToStatus,
      [ISSUE_ATTRIBUTE_NAMES.ASSIGNED_TO]: issue[ISSUE_ATTRIBUTE_NAMES.ASSIGNED_TO],
      [ISSUE_ATTRIBUTE_NAMES.ASSIGNED_TO_TYPE]: issue[ISSUE_ATTRIBUTE_NAMES.ASSIGNED_TO_TYPE] || null,
      ...issueDocProps
    };
  }, [changeToStatus, doc, documentVersion, isClosingIssueWithDocument, issue]);

  const {setCurrentIssuePreviewPopup} = useCurrentIssuePreviewPopup();

  return (
    <Formik
      enableReinitialize
      initialValues={initialValues}
      onSubmit={({[ISSUE_ATTRIBUTE_NAMES.COMMENT]: commentBody, ...values}) => {
        // delete assignedTo/assignedToType from payload if they're unchanged
        if (
          values[ISSUE_ATTRIBUTE_NAMES.ASSIGNED_TO] === issue[ISSUE_ATTRIBUTE_NAMES.ASSIGNED_TO] &&
          values[ISSUE_ATTRIBUTE_NAMES.ASSIGNED_TO_TYPE] === issue[ISSUE_ATTRIBUTE_NAMES.ASSIGNED_TO_TYPE]
        ) {
          delete values[ISSUE_ATTRIBUTE_NAMES.ASSIGNED_TO];
          delete values[ISSUE_ATTRIBUTE_NAMES.ASSIGNED_TO_TYPE];
        }
        const isAddingComment = !!commentBody;

        return new Promise((resolve, reject) => {
          dispatch(
            updateIssueAction(
              issue,
              {
                ...values,
                ...(isAddingComment
                  ? {
                    [ISSUE_ATTRIBUTE_NAMES.COMMENT]: {
                      [ISSUE_ATTRIBUTE_NAMES.COMMENT_BODY]: commentBody
                    }
                  }
                  : {})
              },
              {
                changeToStatus,
                isAddingComment,
                onSuccess: resolve,
                onError: reject
              }
            )
          );
        }).then(
          updatedIssue => {
            const updateTimestamp = new Date(updatedIssue.updatedAt).getTime();
            dispatch(
              fetchActivities({
                issueId: issue.id,
                readType: ActivityBatchReadTypes.LATEST,
                triggerId: updateTimestamp,
                triggerTimestamp: updateTimestamp
              })
            );
            setCurrentIssuePreviewPopup(null);
          },
          e => {
            dispatch(fetchActivities({issueId: issue.id, readType: ActivityBatchReadTypes.LATEST}));
            setCurrentIssuePreviewPopup(null);
            throw e;
          }
        );
      }}
    >
      {({handleSubmit, values, setFieldValue, isSubmitting, isValid}) => {
        const divClassNames = cx('IssuePreviewTransitionPopUpForm', {
          'IssuePreviewTransitionPopUpForm--narrow': viewType === viewTypes.DOCUMENT,
          'IssuePreviewTransitionPopUpForm--wide': viewType === viewTypes.PROJECT
        });

        const saveButtonClasses = cx('IssuePreviewTransitionPopUpForm__save-button', {
          ['IssuePreviewTransitionPopUpForm__save-button--saving']: isSubmitting
        });
        return (
          <div className={divClassNames}>
            <div className="IssuePreviewTransitionPopUpForm__content">
              <div
                className="IssuePreviewTransitionPopUpForm__field"
                data-testid="IssuePreviewTransitionPopUpForm__status-field"
              >
                <Label className="IssuePreviewTransitionPopUpForm__field-label IssuePreviewTransitionPopUpForm__field-label--required">
                  {i18n.t('new.status')}
                </Label>
                <SelectBoxAdapter
                  selectedOptions={issueStatusOptions.find(option => option.value === changeToStatus)}
                  options={issueStatusOptions}
                  optionRenderer={(value, label) => (
                    <StatusColorIndicatorOption statusColor={statusColorMapping[value]} label={label} />
                  )}
                  valueRenderer={(value, label) => (
                    <StatusColorIndicatorOption statusColor={statusColorMapping[value]} label={label} />
                  )}
                  onChange={onStatusChange}
                />
              </div>
              {hasPermittedAttribute(issue, ISSUE_ATTRIBUTE_NAMES.ASSIGNED_TO) && (
                <div
                  className="IssuePreviewTransitionPopUpForm__field"
                  data-testid="IssuePreviewTransitionPopUpForm__assignedTo-field"
                >
                  <Label className="IssuePreviewTransitionPopUpForm__field-label">
                    {i18n.t('transition.assignTo.label')}
                  </Label>
                  <SelectBoxAdapter
                    options={assigneeOptions}
                    selectedOptions={values[ISSUE_ATTRIBUTE_NAMES.ASSIGNED_TO]
                      ? {oxygenId: values[ISSUE_ATTRIBUTE_NAMES.ASSIGNED_TO]}
                      : null}
                    grouped
                    clearable={!requiredFieldsMap[ISSUE_ATTRIBUTE_NAMES.ASSIGNED_TO] && isAssigneeClearable}
                    labelKey="name"
                    valueKey="oxygenId"
                    dropdownMaxWidth={DROPDOWN_MAX_WIDTH}
                    subtextGetter={getUserCompanyName}
                    placeholder={i18n.t('new.placeholder.assignedTo')}
                    searchable
                    searchIcon
                    searchPlaceholder={i18n.t('filter.assignedToSearch')}
                    onChange={value => {
                      setFieldValue(ISSUE_ATTRIBUTE_NAMES.ASSIGNED_TO, value?.oxygenId ?? null);
                      setFieldValue(ISSUE_ATTRIBUTE_NAMES.ASSIGNED_TO_TYPE, value?.oxygenType ?? null);
                    }}
                  />
                </div>
              )}
              {canAddComment(issue) && (
                <div
                  className="IssuePreviewTransitionPopUpForm__field"
                  data-testid="IssuePreviewTransitionPopUpForm__comment-field"
                  key={ISSUE_ATTRIBUTE_NAMES.COMMENT}
                >
                  <Label className="IssuePreviewTransitionPopUpForm__field-label">{i18n.t('comment.label')}</Label>
                  <MentionInput
                    className="IssuePreviewTransitionPopUpForm__mention-input"
                    data-testid="IssuePreviewTransitionPopUpForm__mention-input"
                    suggestions={groupedProjectEntitiesForMention}
                    onChange={value => {
                      const rawCommentText = value?.trim() ?? null;
                      setFieldValue(ISSUE_ATTRIBUTE_NAMES.COMMENT, rawCommentText);
                    }}
                    getSuggestionId={user => user.oxygenId}
                    getSuggestionName={user => user.name}
                    getMentionId={user => user.oxygenId}
                    getMentionName={user => user.name}
                    getMentionType={user => user.oxygenType}
                  />
                </div>
              )}
            </div>
            <div className="IssuePreviewTransitionPopUpForm__footer">
              <Button
                styleType={Button.StyleType.LINK_SECONDARY}
                data-testid="IssuePreviewTransitionPopUpForm__cancel-button"
                onClick={() => setCurrentIssuePreviewPopup(null)}
                disabled={isSubmitting}
              >
                {i18n.t('navigation.cancel')}
              </Button>
              <Button
                styleType={Button.StyleType.PRIMARY}
                className={saveButtonClasses}
                data-testid="IssuePreviewTransitionPopUpForm__saveButton"
                onClick={handleSubmit}
                disabled={isSubmitting || !isValid}
              >
                {isSubmitting
                  ? i18n.t('transition.saving')
                  : changeToStatus === STATUS_VOID
                    ? i18n.t('transition.buttonText.void')
                    : i18n.t('navigation.done')}
              </Button>
            </div>
          </div>
        );
      }}
    </Formik>
  );
};

IssuePreviewTransitionPopUpForm.displayName = 'IssuePreviewTransitionPopUpForm';

IssuePreviewTransitionPopUpForm.propTypes = {
  issue: issueAttributesType,
  document: documentType,
  changeToStatus: PropTypes.string,
  issueStatusOptions: statusOptionsArrayType.isRequired,
  groupedProjectEntitiesForMention: PropTypes.array.isRequired,
  onStatusChange: PropTypes.func.isRequired,
  dispatch: PropTypes.func.isRequired,
  viewType: PropTypes.string.isRequired,
  users: PropTypes.array.isRequired,
  companies: PropTypes.array.isRequired,
  roles: PropTypes.array.isRequired,
  oxygen: oxygenType.isRequired,
  currentUser: oxygenUserType.isRequired,
  requiredFieldsMap: PropTypes.object.isRequired
};

const mapStateToProps = state => ({
  issue: getCurrentPreviewIssueAttributes(state),
  document: getCurrentPreviewIssueDocument(state),
  issueStatusOptions: getCurrentIssuePreviewStatusOptions(state),
  groupedProjectEntitiesForMention: getGroupedProjectActiveEntitiesForMentionComponent(state),
  viewType: getCurrentViewType(state),
  users: getActiveUsers(state),
  companies: getProjectCompanies(state),
  roles: getProjectRoles(state),
  oxygen: getAllOxygenMappings(state),
  currentUser: getCurrentUserInMcFormat(state),
  requiredFieldsMap: getRequiredFieldsFormatted(state)
});

export default connect(mapStateToProps)(IssuePreviewTransitionPopUpForm);
